import AppContext from 'App/AppContext';
import { IPCommandBar, IPLabel, IPPivot, IPPivotItem, IPScrollablePane, IPSpinner } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import Accordion from 'components/Accordion/Accordion';
import ControlGroup from 'components/Accordion/ControlGroup';
import { useAssessmentCommand } from 'components/AuditCriteria/useAssessmentCommand';
import { useSceneAIActionsItem } from 'components/Copilot/useSceneAIActionsItem';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import AddNewLink from 'components/Links/AddNewLink';
import PinnedLinkLink from 'components/Links/PinnedLinkLink';
import { LinkPreviewModalOrUrl } from 'components/Links/Preview/LinkPreviewModalOrUrl';
import { Info } from 'components/Notification/Info';
import { CreateProcessingActivityApprovalModal } from 'components/ProcessingActivity/CreateProcessingActivityApprovaModal';
import {
  hasProcessingActivityDeletePermission,
  hasProcessingActivityUpdatePermission,
} from 'components/ProcessingActivity/ProcessingActivityAuthHelper';
import ReportRequest from 'components/Reports/ReportRequest';
import StringValueMultiSelect from 'components/StringValue/StringValueMultiSelect';
import StringValueSelect from 'components/StringValue/StringValueSelect';
import SingleTask from 'components/Tasks/SingleTask';
import RichTextEditor from 'components/Text/RichTextEditor';
import FadeInOut from 'components/Utils/FadeInOut';
import { PropertySetLabelValue } from 'components/Utils/PropertySet';
import { getEntity } from 'globalFunctions';
import { globalStackTokensGapMedium, globalTextFieldBig } from 'globalStyles';
import { TFunction } from 'i18next';
import Joi from 'joi';
import Asset, { AssetType } from 'models/asset/asset';
import { PermissionTypes } from 'models/auth/rolePermission';
import { ProcessingActivityLinkType } from 'models/dto/processingActivity/processingActivityLinkDTO';
import ReportRequestDTO from 'models/dto/Reports/ReportRequestDTO';
import { ReportRequest20DTO } from 'models/dto/Reports/ReportRequests_20_DTO';
import { EntityTypes } from 'models/entity';
import ProcessingActivity, { PrivacyFramework, ProcessingActivityRole } from 'models/processingActivity';
import ReportDefinition, { ReportType } from 'models/reports/ReportDefinition';
import ResourceLink from 'models/resourceLink';
import { PrivacyFrameworkSetting } from 'models/setting';
import StringValue, { DataZoneKey, StringValueTypes } from 'models/stringValue/stringValue';
import Task from 'models/tasks/task';
import { SystemTaskTypes } from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import { apiGetAssetsContainersOnly } from 'services/Api/assetService';
import {
  apiAddLinkToProcessingActivity,
  apiCreateLink,
  apiUpdateLinkToProcessingActivity,
} from 'services/Api/linkService';
import {
  apiGetProcessingActivities,
  apiGetProcessingActivityCode,
  apiRemoveProcessingActivity,
  apiUpdateProcessingActivity,
} from 'services/Api/processingActivityService';
import { apiCreateTag } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserRolePermission } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnCode } from 'utils/sorting';
import { getRandomId } from 'utils/string';
import { ComplianceModal } from './ComplianceModal';
import { ProcessingActivityAssetList, RelationType } from './ProcessingActivityAssetList';
import { ProcessingResponsibleEntities } from './ProcessingResponsibleEntities';
import { TabDetailsCommandBar } from './TabDetailsCommandBar';
import { TabDetailsEditableHeader } from './TabDetailsEditableHeader';

interface ITabDetailsProps {
  isLoading: boolean;
  selectedProcessingActivity: ProcessingActivity | undefined;
  dbSelectedProcessingActivity: ProcessingActivity | undefined;
  updateProcessingActivity: (processingActivity: ProcessingActivity | undefined) => void;
  updateDbProcessingActivity: (processingActivity: ProcessingActivity) => void;
  showLinkInModal: ResourceLink | undefined;
  setShowLinkInModal: (resouce: ResourceLink | undefined) => void;
  onBadgeClick: (badgeText: string) => void;
  dpiaAnalysisTask: Task | undefined;
  setDpiaAnalysisTask: (task: Task | undefined) => void;
  onPinLink: (item: ResourceLink, pinned: boolean) => void;
  setHasUnsavedChanges: (dirty: boolean) => void;
}

export const TabDetails = (props: ITabDetailsProps) => {
  const { t } = useTranslation(['translation', 'processingActivity', 'processingActivities', 'library']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [selectedReport, setSelectedReport] = useState<ReportDefinition | undefined>(undefined);
  const [reportRequest, setReportRequest] = useState<ReportRequestDTO | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [errorAsync, setErrorAsync] = useState<boolean>(false);
  const [processingActivityList, setProcessingActivityList] = useState<ProcessingActivity[]>([]);
  const [processingActivityListLoading, setProcessingActivityListLoading] = useState<boolean>(false);
  const [removeDialogVisible, setRemoveDialogVisible] = useState<boolean>(false);
  const [richTextEditorsFocusKey, setRichTextEditorsFocusKey] = useState<string>(getRandomId());
  const [selectedProcessingActivityIndex, setSelectedProcessingActivityIndex] = useState<number | undefined>(undefined);
  const [selectedProcessingActivityId, setselectedProcessingActivityId] = useState<number | undefined>(undefined);
  const [showDpiaAnalysis, setShowDpiaAnalysis] = useState<boolean>(false);
  const [showComplianceModal, setShowComplianceModal] = useState<boolean>(false);
  const [complianceModalHasLoaded, setComplianceModalHasLoaded] = useState<boolean>(false);
  const [complianceModalUsesCurrentData, setComplianceModalUsesCurrentData] = useState<boolean>(true);
  const [createNewApprovalModalOpen, setCreateNewApprovalModalOpen] = useState<boolean>(false);
  const [allAssets, setAllAssets] = useState<Asset[] | undefined>(undefined);

  const isReadOnly = !hasProcessingActivityUpdatePermission(appContext, props.selectedProcessingActivity);
  const aiItem = useSceneAIActionsItem();
  const { assessItem, assessDialog } = useAssessmentCommand(
    EntityTypes.ProcessingActivity,
    props.selectedProcessingActivity?.processingActivityId,
    props.selectedProcessingActivity?.name,
  );
  const privacyFramework =
    (appContext.globalDataCache.settings.get(PrivacyFrameworkSetting) as PrivacyFramework) ?? PrivacyFramework.GDPR;
  const standardContractualClauseLinks =
    props.selectedProcessingActivity?.resourceLinks.filter(
      (l) => l.linkTypeId === ProcessingActivityLinkType.StandardContractualClause,
    ) || [];
  const jointControllerLinks =
    props.selectedProcessingActivity?.resourceLinks.filter(
      (l) => l.linkTypeId === ProcessingActivityLinkType.JointControllerAgreement,
    ) || [];

  //
  // Effects
  //
  const shouldWarnOnLeave = hasChanged() && !isReadOnly;

  useEffect(() => {
    if (!shouldWarnOnLeave) return;

    const alertUser = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', alertUser);

    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [shouldWarnOnLeave]);

  useEffect(() => {
    //check for redundant calls for apiGetProcessingActivities after updating current processing activity
    if (
      selectedProcessingActivityId &&
      selectedProcessingActivityId === props.selectedProcessingActivity?.processingActivityId
    )
      return;

    const loadProcessingActivityList = async () => {
      if (processingActivityListLoading === true) return;

      try {
        setProcessingActivityListLoading(true);
        setSelectedProcessingActivityIndex(undefined);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const processingActivities = await apiGetProcessingActivities(accessToken, appContext.globalDataCache);
        processingActivities.sort((a, b) => sortOnCode(a.code, b.code));

        setProcessingActivityList(processingActivities);

        for (let i = 0; i < processingActivities.length; i++) {
          if (processingActivities[i].processingActivityId === props.selectedProcessingActivity?.processingActivityId) {
            setSelectedProcessingActivityIndex(i);
            break;
          }
        }
        setselectedProcessingActivityId(props.selectedProcessingActivity?.processingActivityId);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setProcessingActivityListLoading(false);
      }
    };

    loadProcessingActivityList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedProcessingActivity, selectedProcessingActivityId, processingActivityListLoading]);

  useEffect(() => {
    props.setHasUnsavedChanges(hasChanged());
    // Only re-run when the result of hasChanged() could change:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedProcessingActivity, props.dbSelectedProcessingActivity]);

  useEffect(() => {
    const loadAllAssets = async () => {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const assets = await apiGetAssetsContainersOnly(accessToken, appContext.globalDataCache);
      setAllAssets(assets);
    };

    if (allAssets === undefined) loadAllAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hasChanged(): boolean {
    if (!props.selectedProcessingActivity || !props.dbSelectedProcessingActivity) return false;

    return !props.selectedProcessingActivity.isEqual(props.dbSelectedProcessingActivity);
  }

  const checkExistingCode = async (code: string): Promise<boolean> => {
    try {
      setIsUpdating(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newCode = await apiGetProcessingActivityCode(accessToken, code);

      if (newCode !== '') {
        return true;
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }

    return false;
  };

  useEffect(() => {
    if (!props.selectedProcessingActivity) return;
    const newBaseReportRequest = ReportRequestDTO.getNewRequest(appContext);
    switch (selectedReport?.id) {
      case ReportType.ProcessingActivityDetails:
        const newReport20Request = new ReportRequest20DTO(
          newBaseReportRequest,
          t as TFunction<string[]>,
          privacyFramework,
        );
        newReport20Request.reportDefinition = selectedReport;
        newReport20Request.processingActivityId = props.selectedProcessingActivity.processingActivityId;
        setReportRequest(newReport20Request);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  const saveNewDataToState = (processingActivity: ProcessingActivity) => {
    props.updateProcessingActivity(processingActivity.clone());
    props.updateDbProcessingActivity(processingActivity.clone());
  };

  const onComplianceClick = () => {
    if (!props.selectedProcessingActivity || !props.dbSelectedProcessingActivity) return;
    setShowComplianceModal(true);
  };

  const saveData = async (processingActivityToUpdate?: ProcessingActivity) => {
    if (isUpdating || props.isLoading || !props.selectedProcessingActivity) {
      return;
    }

    try {
      setIsUpdating(true);
      appContext.showContentLoader();

      const newProcessingActivity = processingActivityToUpdate
        ? processingActivityToUpdate
        : props.selectedProcessingActivity;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      newProcessingActivity.tagIds = await createNewTags(newProcessingActivity);
      await apiUpdateProcessingActivity(newProcessingActivity, accessToken, appContext.globalDataCache);
      saveNewDataToState(newProcessingActivity);

      appContext.showNotification(t('translation:General.Notifications.SaveSuccess'));
      setRichTextEditorsFocusKey(getRandomId());
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsUpdating(false);
      if (complianceModalHasLoaded) setComplianceModalUsesCurrentData(false);
    }
  };

  const removeProcessingActivity = async () => {
    if (isUpdating || props.isLoading || !props.selectedProcessingActivity) {
      return;
    }
    try {
      setIsUpdating(true);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiRemoveProcessingActivity(props.selectedProcessingActivity, accessToken);
      history.replace('/processingactivities');
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsUpdating(false);
    }
  };

  const createNewTags = async (processingActivity: ProcessingActivity): Promise<number[]> => {
    const tagids = processingActivity.tagIds || [];
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    for (let idx = 0; idx < processingActivity.newTags.length; idx++) {
      const createdTag = await apiCreateTag(processingActivity.newTags[idx], accessToken);
      tagids.push(createdTag.tagId);
      appContext.globalDataCache.tags.add(createdTag);
    }

    processingActivity.newTags = [];

    return tagids;
  };

  const _localizedFields = {
    code: t('processingActivity:TabDetails.Fields.Code.Label'),
    name: t('processingActivity:TabDetails.Fields.Name.Label'),
  };

  const validate = async (fieldName: string): Promise<string> => {
    if (props.selectedProcessingActivity && hasChanged()) {
      //validate when something has changed
      const result = props.selectedProcessingActivity.validate(_localizedFields);
      setErr(result.error);
      if (result.error) {
        // return the error for the requested fieldname
        const fieldError = result.error.details.find((e) => {
          return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
        });

        if (fieldError) {
          return fieldError.message;
        }
      }

      if (fieldName === 'code') {
        //when field is code, run additional check on back-end to check for duplicates
        if (props.dbSelectedProcessingActivity?.code !== props.selectedProcessingActivity?.code) {
          const exists = await checkExistingCode(props.selectedProcessingActivity.code);
          setErrorAsync(exists);

          if (exists) {
            return t('processingActivity:TabDetails.Fields.Code.Error');
          }
        }
      }
    }

    return Promise.resolve('');
  };

  const getPreviousProcessingActivity = () => {
    if (selectedProcessingActivityIndex === undefined || selectedProcessingActivityIndex === 0) return;
    const processingActivity = processingActivityList[selectedProcessingActivityIndex - 1];
    history.push(`/processingactivity/${processingActivity.processingActivityId}/details`);
  };

  const getNextProcessingActivity = () => {
    if (
      selectedProcessingActivityIndex === undefined ||
      selectedProcessingActivityIndex === processingActivityList.length - 1
    )
      return;
    const processingActivity = processingActivityList[selectedProcessingActivityIndex + 1];
    history.push(`/processingactivity/${processingActivity.processingActivityId}/details`);
  };

  const addRecipients = (assets: Asset[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.recipients.push(...assets);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeRecipient = (item: Asset | undefined) => {
    if (!props.selectedProcessingActivity || !item) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.recipients = newProcessingActivity.recipients.filter((a) => a.assetId !== item.assetId);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addDataAssets = (assets: Asset[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.data.push(...assets);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeDataAsset = (item: Asset | undefined) => {
    if (!props.selectedProcessingActivity || !item) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.data = newProcessingActivity.data.filter((a) => a.assetId !== item.assetId);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addProcessors = (assets: Asset[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.processors.push(...assets);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeProcessor = (item: Asset | undefined) => {
    if (!props.selectedProcessingActivity || !item) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.processors = newProcessingActivity.processors.filter((a) => a.assetId !== item.assetId);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addDataSubjects = (dataSubjects: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.dataSubjects.push(...dataSubjects);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeDataSubjects = (dataSubjects: StringValue[]) => {
    if (!props.selectedProcessingActivity || !dataSubjects) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.dataSubjects = newProcessingActivity.dataSubjects.filter(
      (ds) => !dataSubjects.some((rm) => rm.stringValueId === ds.stringValueId),
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addDataCategories = (dataCategories: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.dataCategories.push(...dataCategories);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeDataCategories = (dataCategories: StringValue[]) => {
    if (!props.selectedProcessingActivity || !dataCategories) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.dataCategories = newProcessingActivity.dataCategories.filter(
      (ds) => !dataCategories.some((rm) => rm.stringValueId === ds.stringValueId),
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addLegalBasis = (stringValues: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.legalBasis.push(...stringValues);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeLegalBasis = (stringValue: StringValue[]) => {
    if (!props.selectedProcessingActivity || !stringValue) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.legalBasis = newProcessingActivity.legalBasis.filter(
      (lb) => !stringValue.some((rm) => rm.stringValueId === lb.stringValueId),
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addExecutingEntities = (stringValues: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.executingEntities.push(...stringValues);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeExecutingEntities = (stringValues: StringValue[]) => {
    if (!props.selectedProcessingActivity || !stringValues) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.executingEntities = newProcessingActivity.executingEntities.filter(
      (ee) => !stringValues.some((rm) => rm.stringValueId === ee.stringValueId),
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addJointControllers = (assets: Asset[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.joinControllers.push(...assets);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeJointController = (item: Asset | undefined) => {
    if (!props.selectedProcessingActivity || !item) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.joinControllers = newProcessingActivity.joinControllers.filter(
      (a) => a.assetId !== item.assetId,
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const addOtherRecipients = (stringValues: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.recipientCategories.push(...stringValues);
    props.updateProcessingActivity(newProcessingActivity);
  };

  const removeOtherRecipients = (stringValues: StringValue[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.recipientCategories = newProcessingActivity.recipientCategories.filter(
      (rr) => !stringValues.some((rm) => rm.stringValueId === rr.stringValueId),
    );
    props.updateProcessingActivity(newProcessingActivity);
  };

  const setResponsibleTenants = (tenantIds: string[]) => {
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.responsibleEntitiesTenants = tenantIds;
    props.updateProcessingActivity(newProcessingActivity);
  };

  const setDataSubjectScale = (selection: StringValue | undefined) => {
    if (!selection) return;
    if (!props.selectedProcessingActivity) return;
    const newProcessingActivity = props.selectedProcessingActivity.clone();
    newProcessingActivity.dataSubjectScale = selection;
    props.updateProcessingActivity(newProcessingActivity);
  };

  const openDpiaAnalysis = () => {
    setShowDpiaAnalysis(true);
  };

  const getNewDPIATask = (): Task | undefined => {
    if (props.selectedProcessingActivity === undefined) return undefined;
    const newTask = Task.getEmptyTaskWithStates(appContext.globalDataCache.taskStates.items);
    newTask.relatedEntities = [getEntity(props.selectedProcessingActivity)];
    newTask.addEntities(newTask.relatedEntities);
    newTask.userId = props.selectedProcessingActivity.ownerId;
    newTask.creatorId = appContext.user.id;
    newTask.ownerId = appContext.user.id;
    newTask.authSchemaId = props.selectedProcessingActivity.authSchemaId;
    newTask.name = t('processingActivity:DPIA.Task.Prefix', { name: props.selectedProcessingActivity.name });
    newTask.description = t('processingActivity:DPIA.Task.Description', {
      name: props.selectedProcessingActivity.name,
    });

    //apply DPIA form if it is found
    const taskTypeDPIA = appContext.globalDataCache.taskTypes.getTypeOfSystemType(SystemTaskTypes.DPIA);
    if (taskTypeDPIA) {
      newTask.taskTypeId = taskTypeDPIA.taskTypeId;
    }

    props.setDpiaAnalysisTask(newTask);

    return newTask;
  };

  const getDefaultActiveFromLocalState = (): string | undefined => {
    const localState = getLocalStorageData(appContext, LocalStorageKeys.ProcessingActivitySelectedPivot);

    return localState;
  };

  const setDefaultActiveToLocalState = (key: string) => {
    setLocalStorageData(appContext, LocalStorageKeys.ProcessingActivitySelectedPivot, key);
  };

  const addLinkType = async (links: ResourceLink[], linkType: ProcessingActivityLinkType) => {
    if (!props.selectedProcessingActivity) return;
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      for (let idx = 0; idx < links.length; idx++) {
        const item = links[idx];
        item.linkTypeId = linkType;
        item.pinned = true;
        if (item.linkId <= 0) {
          //new link, create and add the connection
          await appContext.globalDataCache.lists.getItems();
          const newLinkCreated = await apiCreateLink(item, accessToken, appContext.globalDataCache);
          await apiAddLinkToProcessingActivity(
            newLinkCreated.linkId,
            props.selectedProcessingActivity.processingActivityId,
            linkType,
            newLinkCreated.linkURLFragment,
            accessToken,
          );
          links[idx] = newLinkCreated;
        } else if (props.selectedProcessingActivity.resourceLinks.some((l) => l.linkId === item.linkId)) {
          //existing link that is already connected, update the connection
          await apiUpdateLinkToProcessingActivity(
            item.linkId,
            props.selectedProcessingActivity.processingActivityId,
            linkType,
            item.linkURLFragment,
            accessToken,
          );
        } else {
          //existing link, add the connection
          await apiAddLinkToProcessingActivity(
            item.linkId,
            props.selectedProcessingActivity.processingActivityId,
            linkType,
            item.linkURLFragment,
            accessToken,
          );
        }
      }

      const newProcessingActivity = props.selectedProcessingActivity.clone();
      newProcessingActivity.resourceLinks = newProcessingActivity.resourceLinks.filter(
        (l) => !links.some((link) => link.linkId === l.linkId),
      );
      newProcessingActivity.resourceLinks.push(...links);
      props.updateProcessingActivity(newProcessingActivity);
      for (const l of links) {
        props.onPinLink(l, true);
      }
    } catch {
      appContext.showNotification(t('library:LinkComponents.ErrorMessageAdd'), true);
    }
  };

  const removeLinkType = async (link: ResourceLink) => {
    if (
      !props.selectedProcessingActivity ||
      props.selectedProcessingActivity.resourceLinks.find((l) => l.linkId === link.linkId) === undefined
    )
      return;
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUpdateLinkToProcessingActivity(
        link.linkId,
        props.selectedProcessingActivity.processingActivityId,
        ProcessingActivityLinkType.Default,
        undefined,
        accessToken,
      );

      const newProcessingActivity = props.selectedProcessingActivity.clone();
      newProcessingActivity.resourceLinks.find((l) => l.linkId === link.linkId)!.linkTypeId =
        ProcessingActivityLinkType.Default;
      newProcessingActivity.resourceLinks.find((l) => l.linkId === link.linkId)!.pinned = false;
      props.updateProcessingActivity(newProcessingActivity);
      props.onPinLink(link, false);
    } catch {
      appContext.showNotification(t('library:LinkComponents.ErrorMessageRemove'), true);
    }
  };

  //
  // Main render
  //
  return (
    <Fragment>
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <Prompt when={hasChanged() && !isReadOnly} message={t('translation:General.Notifications.LeaveDirtyPage')} />
        {!isReadOnly && (
          <IPStack.Item>
            <TabDetailsCommandBar
              saveDisabled={props.isLoading || isUpdating || !hasChanged() || err !== undefined || errorAsync}
              discardDisabled={props.isLoading || isUpdating}
              removeDisabled={
                props.isLoading ||
                isUpdating ||
                !hasProcessingActivityDeletePermission(appContext, props.selectedProcessingActivity)
              }
              openDpiaAnalysisDisabled={props.isLoading || isUpdating}
              onOpenDpiaAnalysis={openDpiaAnalysis}
              prevDisabled={
                props.isLoading ||
                isUpdating ||
                selectedProcessingActivityIndex === undefined ||
                selectedProcessingActivityIndex === 0
              }
              nextDisabled={
                props.isLoading ||
                isUpdating ||
                selectedProcessingActivityIndex === undefined ||
                selectedProcessingActivityIndex === processingActivityList.length - 1
              }
              onRemove={() => setRemoveDialogVisible(true)}
              onDiscard={() => {
                props.updateProcessingActivity(props.dbSelectedProcessingActivity);
                setRichTextEditorsFocusKey(getRandomId());
              }}
              onSave={saveData}
              onPrevProcessingActivity={() => {
                getPreviousProcessingActivity();
              }}
              onNextProcessingActivity={() => {
                getNextProcessingActivity();
              }}
              setSelectedReport={setSelectedReport}
              complianceDisabled={
                props.isLoading ||
                isUpdating ||
                (hasChanged() && !complianceModalHasLoaded) ||
                err !== undefined ||
                errorAsync
              }
              onComplianceClick={onComplianceClick}
              showApprovalButton={!hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity)}
              onClickApproval={() => setCreateNewApprovalModalOpen(true)}
              approvalDisabled={props.isLoading || isUpdating || hasChanged()}
              assessItem={assessItem}
              aiItem={aiItem}
            />
          </IPStack.Item>
        )}
        {/* a reader gets the AI actions alone: Explain and What is missing are for them too */}
        {isReadOnly && aiItem && (
          <IPStack.Item>
            <IPCommandBar items={[aiItem]} styles={{ root: { padding: 0 } }} />
          </IPStack.Item>
        )}
        {props.isLoading && (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!props.isLoading && (
          <FadeInOut show={true} duration={350} styles={{ flexGrow: 1, height: '100%' }}>
            <IPStack verticalFill tokens={globalStackTokensGapMedium}>
              {/* Wrapped rest of the view into scrollable pane*/}
              <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
                <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
                  <IPStack verticalFill tokens={globalStackTokensGapMedium} styles={{ root: { paddingRight: 10 } }}>
                    {/* Title */}
                    <IPStack.Item>
                      <IPTextField
                        styles={globalTextFieldBig}
                        underlined
                        placeholder={t('processingActivity:TabDetails.Fields.Name.Placeholder')}
                        value={props.selectedProcessingActivity?.name ?? ''}
                        onGetErrorMessage={() => {
                          return validate('name');
                        }}
                        onChange={(
                          event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                          newValue?: string,
                        ) => {
                          const newProcessingActivity = props.selectedProcessingActivity?.clone();
                          if (newProcessingActivity) {
                            newProcessingActivity.name = newValue ? newValue.trimStart() : '';
                            props.updateProcessingActivity(newProcessingActivity);
                          }
                        }}
                        readOnly={isReadOnly}
                      />
                    </IPStack.Item>
                    {props.selectedProcessingActivity && (
                      <IPStack.Item>
                        <TabDetailsEditableHeader
                          processingActivity={props.selectedProcessingActivity}
                          updateProcessingActivity={props.updateProcessingActivity}
                          onCodeErrorMessage={() => {
                            return validate('code');
                          }}
                          readOnly={isReadOnly}
                          unFocusKey={richTextEditorsFocusKey}
                          setSelectedReport={setSelectedReport}
                        />
                      </IPStack.Item>
                    )}

                    {props.selectedProcessingActivity && (
                      <IPStack.Item>
                        <ControlGroup
                          title={ProcessingActivity.getTransfersTextLabel(t, privacyFramework)}
                          summary={props.selectedProcessingActivity.getNumberOfTransfersText(t as TFunction<string[]>)}
                          showBorder={true}
                          defaultActive={
                            getLocalStorageData(appContext, LocalStorageKeys.ProcessingActivityTransfersAccordion) ===
                            'true'
                          }
                          onToggle={(isActive: boolean) => {
                            setLocalStorageData(
                              appContext,
                              LocalStorageKeys.ProcessingActivityTransfersAccordion,
                              isActive.toString(),
                            );
                          }}
                        >
                          <IPStack
                            horizontal
                            tokens={globalStackTokensGapMedium}
                            verticalAlign="center"
                            styles={{ root: { padding: 10 } }}
                          >
                            <PropertySetLabelValue
                              label={ProcessingActivity.getTransfersTextLabel(t, privacyFramework)}
                              value={props.selectedProcessingActivity.getNumberOfTransfersText(
                                t as TFunction<string[]>,
                              )}
                            />
                            <PropertySetLabelValue
                              label={t('processingActivity:TabDetails.Fields.SafeGuards.Label')}
                              value={props.selectedProcessingActivity.getRequiredSafeGuardsText(
                                t as TFunction<string[]>,
                                privacyFramework,
                              )}
                            />
                            <Info
                              message={
                                props.selectedProcessingActivity.getDataZoneKey() === DataZoneKey.Other ? (
                                  <>
                                    <span>
                                      {privacyFramework === PrivacyFramework.SwissDSG
                                        ? t('processingActivity:TabDetails.Fields.SafeGuards.OtherSwissInfo')
                                        : t('processingActivity:TabDetails.Fields.SafeGuards.OtherGDPRInfo')}
                                    </span>
                                    <br />
                                    <span>{t('processingActivity:TabDetails.Fields.EEATransfer.Info')}</span>
                                  </>
                                ) : (
                                  t('processingActivity:TabDetails.Fields.EEATransfer.Info')
                                )
                              }
                            />
                          </IPStack>
                          {props.selectedProcessingActivity.getDataZoneKey() === DataZoneKey.Other && (
                            <IPStack styles={{ root: { paddingLeft: 20, paddingBottom: 20, paddingRight: 20 } }}>
                              <IPStack.Item>
                                <IPLabel>{t('processingActivity:TabDetails.Fields.EEATransfer.LinkLabel')}</IPLabel>
                              </IPStack.Item>
                              <IPStack horizontalAlign="start" horizontal>
                                <IPStack.Item>
                                  <AddNewLink
                                    addLinks={(link) =>
                                      addLinkType(link, ProcessingActivityLinkType.StandardContractualClause)
                                    }
                                    existingLinks={standardContractualClauseLinks}
                                    disabled={isReadOnly}
                                    hideWholeList={true}
                                    allowCustomLists={true}
                                    allowDocumentLibraries={true}
                                    allowWebURLs={false}
                                    allowSitePages={true}
                                    entityContext={true}
                                    showCategoryContents={true}
                                  />
                                </IPStack.Item>
                                {standardContractualClauseLinks.map((link) => (
                                  <IPStack.Item key={link.linkId} style={{ marginLeft: 24 }}>
                                    <PinnedLinkLink
                                      key={link.linkId}
                                      link={link}
                                      onClick={(link) => props.onBadgeClick(link.linkName)}
                                      onDelete={(link) => removeLinkType(link)}
                                      readonly={isReadOnly}
                                    />
                                  </IPStack.Item>
                                ))}
                              </IPStack>
                            </IPStack>
                          )}
                        </ControlGroup>
                      </IPStack.Item>
                    )}
                    <IPStack.Item grow>
                      <IPPivot
                        styles={{ root: { paddingBottom: 20 } }}
                        defaultSelectedKey={getDefaultActiveFromLocalState()}
                        onLinkClick={(item) => setDefaultActiveToLocalState(item?.props.itemKey ?? '')}
                      >
                        <IPPivotItem itemKey="purpose" headerText={t('processingActivity:TabDetails.Pivot.Purpose')}>
                          <IPStack tokens={globalStackTokensGapMedium}>
                            <IPStack.Item>
                              <RichTextEditor
                                label={t('processingActivity:TabDetails.Fields.Purpose.Label')}
                                labelInfo={t('processingActivity:TabDetails.Fields.Purpose.Info')}
                                html={props.selectedProcessingActivity?.purpose}
                                onChange={(value: string) => {
                                  const newProcessingActivity = props.selectedProcessingActivity?.clone();
                                  if (newProcessingActivity) {
                                    newProcessingActivity.purpose = value;
                                    props.updateProcessingActivity(newProcessingActivity);
                                  }
                                }}
                                placeholder={t('processingActivity:TabDetails.Fields.Purpose.Placeholder')}
                                maxHeight={-1}
                                readOnly={isReadOnly}
                                unFocusKey={richTextEditorsFocusKey}
                                onBadgeClick={props.onBadgeClick}
                                aiField={
                                  props.selectedProcessingActivity
                                    ? {
                                        entityType: EntityTypes.ProcessingActivity,
                                        entityId: props.selectedProcessingActivity.processingActivityId,
                                        field: 'purpose',
                                        entityName: props.selectedProcessingActivity.name,
                                        label: t('processingActivity:TabDetails.Fields.Purpose.Label'),
                                      }
                                    : undefined
                                }
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <RichTextEditor
                                label={t('processingActivity:TabDetails.Fields.Description.Label')}
                                labelInfo={t('processingActivity:TabDetails.Fields.Description.Info')}
                                html={props.selectedProcessingActivity?.description}
                                onChange={(value: string) => {
                                  const newProcessingActivity = props.selectedProcessingActivity?.clone();
                                  if (newProcessingActivity) {
                                    newProcessingActivity.description = value;
                                    props.updateProcessingActivity(newProcessingActivity);
                                  }
                                }}
                                placeholder={t('processingActivity:TabDetails.Fields.Description.Placeholder')}
                                maxHeight={-1}
                                readOnly={isReadOnly}
                                unFocusKey={richTextEditorsFocusKey}
                                onBadgeClick={props.onBadgeClick}
                                aiField={
                                  props.selectedProcessingActivity
                                    ? {
                                        entityType: EntityTypes.ProcessingActivity,
                                        entityId: props.selectedProcessingActivity.processingActivityId,
                                        field: 'description',
                                        entityName: props.selectedProcessingActivity.name,
                                        label: t('processingActivity:TabDetails.Fields.Description.Label'),
                                      }
                                    : undefined
                                }
                              />
                            </IPStack.Item>
                          </IPStack>
                        </IPPivotItem>
                        <IPPivotItem itemKey="data" headerText={t('processingActivity:TabDetails.Pivot.Data')}>
                          <IPStack tokens={globalStackTokensGapMedium}>
                            <IPStack.Item>
                              <ProcessingActivityAssetList
                                label={t('processingActivity:TabDetails.AssetList.Label.Data')}
                                info={t('processingActivity:TabDetails.AssetList.Label.DataInfo')}
                                assets={props.selectedProcessingActivity?.data ?? []}
                                isLoading={props.isLoading}
                                isReadOnly={isReadOnly}
                                selectedProcessingActivity={props.selectedProcessingActivity}
                                addAssets={addDataAssets}
                                removeAsset={removeDataAsset}
                                assetFilter={(asset: Asset) => asset.type === AssetType.Information}
                                relationType={RelationType.Data}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <StringValueMultiSelect
                                label={t('processingActivity:TabDetails.StringValueLabels.DataSubjects')}
                                info={t('processingActivity:TabDetails.StringValueLabels.DataSubjectsInfo')}
                                isReadOnly={isReadOnly}
                                selectedStringValues={props.selectedProcessingActivity?.dataSubjects ?? []}
                                stringValueType={StringValueTypes.DataSubject}
                                addStringValues={addDataSubjects}
                                removeStringValues={removeDataSubjects}
                                canCreateNew={true}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <Accordion
                                title={t('processingActivity:TabDetails.Fields.DataSubjectScale')}
                                info={t('processingActivity:TabDetails.Fields.DataSubjectScaleInfo')}
                                summary={props.selectedProcessingActivity?.dataSubjectScale?.value || ''}
                                showBorder={true}
                              >
                                <IPStack
                                  verticalFill
                                  tokens={globalStackTokensGapMedium}
                                  styles={{ root: { padding: '10px' } }}
                                >
                                  <StringValueSelect
                                    stringValueType={StringValueTypes.DataSubjectScale}
                                    canAddNew={true}
                                    onChange={setDataSubjectScale}
                                    currentValueId={props.selectedProcessingActivity?.dataSubjectScale?.stringValueId}
                                    isReadOnly={isReadOnly}
                                  />
                                </IPStack>
                              </Accordion>
                            </IPStack.Item>
                            <IPStack.Item>
                              <StringValueMultiSelect
                                label={t('processingActivity:TabDetails.StringValueLabels.DataCategories')}
                                info={t('processingActivity:TabDetails.StringValueLabels.DataCategoriesInfo')}
                                isReadOnly={isReadOnly}
                                selectedStringValues={props.selectedProcessingActivity?.dataCategories ?? []}
                                stringValueType={StringValueTypes.DataCategory}
                                addStringValues={addDataCategories}
                                removeStringValues={removeDataCategories}
                                canCreateNew={true}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <StringValueMultiSelect
                                label={t('processingActivity:TabDetails.StringValueLabels.LegalBasis')}
                                info={t('processingActivity:TabDetails.StringValueLabels.LegalBasisInfo')}
                                isReadOnly={isReadOnly}
                                selectedStringValues={props.selectedProcessingActivity?.legalBasis ?? []}
                                stringValueType={StringValueTypes.LegalBasis}
                                addStringValues={addLegalBasis}
                                removeStringValues={removeLegalBasis}
                                canCreateNew={false}
                              />
                            </IPStack.Item>
                          </IPStack>
                        </IPPivotItem>
                        <IPPivotItem itemKey="roles" headerText={t('processingActivity:TabDetails.Pivot.Roles')}>
                          <IPStack tokens={globalStackTokensGapMedium}>
                            <IPStack.Item>
                              <ProcessingActivityAssetList
                                label={t('processingActivity:TabDetails.AssetList.Label.Processors')}
                                info={t('processingActivity:TabDetails.AssetList.Label.ProcessorsInfo')}
                                assets={props.selectedProcessingActivity?.processors ?? []}
                                isLoading={props.isLoading}
                                isReadOnly={isReadOnly}
                                selectedProcessingActivity={props.selectedProcessingActivity}
                                addAssets={addProcessors}
                                removeAsset={removeProcessor}
                                assetFilter={(asset: Asset) => asset.type === AssetType.Supplier}
                                relationType={RelationType.Processor}
                                allAssets={allAssets}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <ProcessingActivityAssetList
                                label={t('processingActivity:TabDetails.AssetList.Label.Recipients')}
                                info={t('processingActivity:TabDetails.AssetList.Label.RecipientsInfo')}
                                assets={props.selectedProcessingActivity?.recipients ?? []}
                                isLoading={props.isLoading}
                                isReadOnly={isReadOnly}
                                selectedProcessingActivity={props.selectedProcessingActivity}
                                addAssets={addRecipients}
                                removeAsset={removeRecipient}
                                assetFilter={(asset: Asset) => asset.type === AssetType.Supplier}
                                relationType={RelationType.Recipient}
                                allAssets={allAssets}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <StringValueMultiSelect
                                label={t('processingActivity:TabDetails.Fields.OtherRecipients')}
                                info={t('processingActivity:TabDetails.Fields.OtherRecipientsInfo')}
                                isReadOnly={isReadOnly}
                                selectedStringValues={props.selectedProcessingActivity?.recipientCategories ?? []}
                                stringValueType={StringValueTypes.RecipientCategory}
                                addStringValues={addOtherRecipients}
                                removeStringValues={removeOtherRecipients}
                                canCreateNew={true}
                              />
                            </IPStack.Item>
                            <IPStack.Item>
                              <ProcessingActivityAssetList
                                subTitle={
                                  props.selectedProcessingActivity?.role === ProcessingActivityRole.Processor
                                    ? t('processingActivity:TabDetails.ControllerSubTitle')
                                    : t('processingActivity:TabDetails.JointControllerSubTitle')
                                }
                                label={
                                  props.selectedProcessingActivity?.role === ProcessingActivityRole.Processor
                                    ? t('processingActivity:TabDetails.AssetList.Label.Controllers')
                                    : t('processingActivity:TabDetails.AssetList.Label.JointControllers')
                                }
                                info={
                                  props.selectedProcessingActivity?.role === ProcessingActivityRole.Processor
                                    ? t('processingActivity:TabDetails.AssetList.Label.ControllersInfo')
                                    : t('processingActivity:TabDetails.AssetList.Label.JointControllersInfo')
                                }
                                assets={props.selectedProcessingActivity?.joinControllers ?? []}
                                isLoading={props.isLoading}
                                isReadOnly={isReadOnly}
                                selectedProcessingActivity={props.selectedProcessingActivity}
                                addAssets={addJointControllers}
                                removeAsset={removeJointController}
                                assetFilter={(asset: Asset) => asset.type === AssetType.Supplier}
                                relationType={RelationType.JointController}
                                links={jointControllerLinks}
                                addLinks={(links) =>
                                  addLinkType(links, ProcessingActivityLinkType.JointControllerAgreement)
                                }
                                removeLink={(link) => removeLinkType(link)}
                                onLinkClick={(linkName) => props.onBadgeClick(linkName)}
                                allAssets={allAssets}
                              />
                            </IPStack.Item>
                            {props.selectedProcessingActivity?.role === ProcessingActivityRole.Controller && (
                              <IPStack.Item>
                                <ProcessingResponsibleEntities
                                  selectedProcessingActivity={props.selectedProcessingActivity}
                                  setResponsibleTenants={setResponsibleTenants}
                                />
                              </IPStack.Item>
                            )}
                            <IPStack.Item>
                              <StringValueMultiSelect
                                label={t('processingActivity:TabDetails.StringValueLabels.ExecutingEntities')}
                                info={t('processingActivity:TabDetails.StringValueLabels.ExecutingEntitiesInfo')}
                                isReadOnly={isReadOnly}
                                selectedStringValues={props.selectedProcessingActivity?.executingEntities ?? []}
                                stringValueType={StringValueTypes.ExecutingEntity}
                                addStringValues={addExecutingEntities}
                                removeStringValues={removeExecutingEntities}
                                canCreateNew={true}
                              />
                            </IPStack.Item>
                          </IPStack>
                        </IPPivotItem>
                      </IPPivot>
                    </IPStack.Item>
                  </IPStack>
                </IPScrollablePane>
              </IPStack.Item>
            </IPStack>
          </FadeInOut>
        )}
        <LinkPreviewModalOrUrl
          isOpen={props.showLinkInModal !== undefined}
          onClose={() => props.setShowLinkInModal(undefined)}
          link={props.showLinkInModal}
          confirmOpen={false}
          enableLinks={true}
        />
        {/* Remove Confirmation Dialog */}
        <IPStack.Item>
          <DialogConfirmDelete
            hidden={!removeDialogVisible}
            onNo={() => {
              setRemoveDialogVisible(false);
            }}
            onYes={() => {
              removeProcessingActivity();
              setRemoveDialogVisible(false);
            }}
            subText={t('processingActivity:PopUp.Remove.SubTitle')}
            title={t('processingActivity:PopUp.Remove.Title', { name: props.selectedProcessingActivity?.name })}
          />
        </IPStack.Item>
        {showDpiaAnalysis && (
          <SingleTask
            task={props.dpiaAnalysisTask ?? getNewDPIATask() ?? new Task()}
            isOpen={showDpiaAnalysis}
            close={() => setShowDpiaAnalysis(false)}
            onSave={(task: Task, isNew: boolean, isCancelled: boolean) => {
              if (isCancelled) return;
              props.setDpiaAnalysisTask(task);
              if (isNew) {
                const newProcessingActivity = props.dbSelectedProcessingActivity?.clone();
                if (newProcessingActivity) {
                  newProcessingActivity.taskId_DPIA = task.taskId;
                  saveData(newProcessingActivity);
                }
              }
            }}
            onRemove={(task: Task) => {
              const newProcessingActivity = props.dbSelectedProcessingActivity?.clone();
              if (newProcessingActivity) {
                newProcessingActivity.taskId_DPIA = undefined;
                props.setDpiaAnalysisTask(undefined);
                saveData(newProcessingActivity);
              }
            }}
            onUpdate={() => {}}
          />
        )}
        <ReportRequest
          isOpen={reportRequest !== undefined}
          onClose={() => setReportRequest(undefined)}
          reportRequest={reportRequest}
        />
        <ComplianceModal
          isOpen={showComplianceModal}
          onClose={() => setShowComplianceModal(false)}
          onSave={saveData}
          processingActivity={props.selectedProcessingActivity}
          hasChanged={hasChanged()}
          setHasLoaded={setComplianceModalHasLoaded}
          modelUpToDate={complianceModalUsesCurrentData}
          setModelUpToDate={setComplianceModalUsesCurrentData}
        />
        {createNewApprovalModalOpen && (
          <CreateProcessingActivityApprovalModal
            isOpen={createNewApprovalModalOpen}
            onClose={() => setCreateNewApprovalModalOpen(false)}
            selectedProcessingActivities={props.selectedProcessingActivity ? [props.selectedProcessingActivity] : []}
            updateProcessingActivities={(processingActivities: ProcessingActivity[]) => {
              if (processingActivities.length === 0) return;
              props.updateProcessingActivity(processingActivities[0]);
              props.updateDbProcessingActivity(processingActivities[0]);
            }}
          />
        )}
      </IPStack>
      {assessDialog}
    </Fragment>
  );
};
